import type { WhatsAppTemplate } from '@/hooks/useMetaTemplates';
import { Image, Video, FileText, MapPin, Link, Phone, MessageSquare } from 'lucide-react';

type Template = Partial<WhatsAppTemplate>;

const parseJsonSafely = (value: any) => {
  if (typeof value !== 'string') return value;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch (error) {
    return null;
  }
};

const getComponentsArray = (template: any): any[] => {
  if (Array.isArray(template?.components)) return template.components as any[];

  const parsedComponents = parseJsonSafely(template?.components);
  if (Array.isArray(parsedComponents)) return parsedComponents;

  const legacy = template?.components;
  if (legacy && typeof legacy === 'object') {
    const collected: any[] = [];
    if (legacy.header) collected.push({ type: 'HEADER', ...legacy.header });
    if (legacy.body) collected.push({ type: 'BODY', ...legacy.body });
    if (legacy.footer) collected.push({ type: 'FOOTER', ...legacy.footer });
    if (legacy.buttons) collected.push({ type: 'BUTTONS', ...legacy.buttons });
    if (legacy.carousel) collected.push({ type: 'CAROUSEL', ...legacy.carousel });
    return collected;
  }

  const meta = parseJsonSafely(template?.meta_response) || template?.meta_response;
  if (meta?.components && Array.isArray(meta.components)) {
    return meta.components as any[];
  }

  return [];
};

const getFirstComponentOfType = (template: any, type: string): any | undefined => {
  const comps = getComponentsArray(template);
  return comps.find((c: any) => (c?.type || '').toUpperCase() === type);
};

const getAuthCodeSample = (bodyComponent: any): string => {
  const example = bodyComponent?.example;
  if (example) {
    if (Array.isArray(example.body_text)) {
      const first = example.body_text[0];
      if (Array.isArray(first) && first.length > 0 && first[0]) return String(first[0]);
      if (typeof first === 'string' && first) return first;
    }
    const namedParams = example.body_text_named_params;
    if (Array.isArray(namedParams) && namedParams.length > 0) {
      const param = namedParams[0];
      if (param?.example) return String(param.example);
      if (param?.param_name) return `{{${param.param_name}}}`;
    }
  }
  return '{{1}}';
};

const composeAuthBodyText = (bodyComponent: any): string => {
  const code = getAuthCodeSample(bodyComponent);
  let text = `${code} is your verification code.`;
  if (bodyComponent?.add_security_recommendation === true) {
    text += '\n\nFor your security, do not share this code.';
  }
  return text;
};

const getTemplatePreviewText = (template: Template): string => {
  const category = (template?.category || '').toString().toUpperCase();
  if (category === 'AUTHENTICATION') {
    const authBody = getFirstComponentOfType(template, 'BODY');
    const inlineText = authBody?.text && String(authBody.text).trim();
    if (!inlineText) {
      return composeAuthBodyText(authBody);
    }
  }

  if (template.body_text && template.body_text.trim()) {
    return template.body_text;
  }
  if ((template as any).bodyText && String((template as any).bodyText).trim()) {
    return String((template as any).bodyText);
  }

  const body = getFirstComponentOfType(template, 'BODY');
  if (body?.text && body.text.trim()) {
    return body.text as string;
  }
  const exampleBody = body?.example?.body_text?.[0];
  if (Array.isArray(exampleBody) && exampleBody.length) {
    return exampleBody.join(' ');
  }

  const header = getFirstComponentOfType(template, 'HEADER');
  if (header?.text) return header.text as string;
  const exampleHeader = header?.example?.header_text?.[0];
  if (exampleHeader) return exampleHeader as string;

  const carousel = getFirstComponentOfType(template, 'CAROUSEL');
  const firstCardText = carousel?.cards?.[0]?.body?.text
    || carousel?.cards?.[0]?.components?.find((c: any) => c?.type === 'BODY')?.text;
  if (firstCardText) return firstCardText as string;

  const anyComponentWithText = getComponentsArray(template).find((c: any) => typeof c?.text === 'string' && c.text.trim());
  if (anyComponentWithText?.text) return anyComponentWithText.text as string;

  const meta = parseJsonSafely((template as any).meta_response) || (template as any).meta_response;
  if (meta?.body_text && String(meta.body_text).trim()) {
    return String(meta.body_text);
  }

  const metaComponents = Array.isArray(meta?.components) ? meta.components : [];
  const metaBody = metaComponents.find((c: any) => (c?.type || '').toUpperCase() === 'BODY');
  if (metaBody?.text && String(metaBody.text).trim()) {
    return String(metaBody.text);
  }
  const metaExampleBody = metaBody?.example?.body_text?.[0];
  if (Array.isArray(metaExampleBody) && metaExampleBody.length) {
    return metaExampleBody.join(' ');
  }

  const buttonsComp = getFirstComponentOfType(template, 'BUTTONS');
  const buttonsArray: any[] = Array.isArray((buttonsComp as any)?.buttons) ? (buttonsComp as any).buttons : [];
  if (buttonsArray.length) {
    return `Buttons: ${buttonsArray.map((b: any) => b?.text).filter(Boolean).join(' | ')}`;
  }

  return 'No preview available';
};

const getHeaderFormatIcon = (format: string) => {
  switch (format) {
    case 'IMAGE': return <Image className="h-4 w-4" />;
    case 'VIDEO': return <Video className="h-4 w-4" />;
    case 'DOCUMENT': return <FileText className="h-4 w-4" />;
    case 'LOCATION': return <MapPin className="h-4 w-4" />;
    default: return <MessageSquare className="h-4 w-4" />;
  }
};

const getButtonIcon = (buttonType: string) => {
  switch (buttonType) {
    case 'URL': return <Link className="h-3 w-3" />;
    case 'PHONE':
    case 'PHONE_NUMBER': return <Phone className="h-3 w-3" />;
    default: return <MessageSquare className="h-3 w-3" />;
  }
};

export function InboxTemplatePreview({
  template,
  className = '',
}: {
  template: Template;
  className?: string;
}) {
  const headerComponent = getFirstComponentOfType(template, 'HEADER');
  const footerComponent = getFirstComponentOfType(template, 'FOOTER');
  const buttonsComponent = getFirstComponentOfType(template, 'BUTTONS');
  const carouselComponent = getFirstComponentOfType(template, 'CAROUSEL');

  const renderHeader = () => {
    if (!headerComponent) return null;

    return (
      <div className="space-y-2">
        {headerComponent.format === 'TEXT' ? (
          <div className="text-sm font-medium text-gray-800 bg-gray-50 p-2 rounded">
            {headerComponent.text}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              {getHeaderFormatIcon(headerComponent.format || 'TEXT')}
              <span className="capitalize">{headerComponent.format?.toLowerCase()}</span>
            </div>
            <div className="bg-gray-100 rounded-lg p-2 flex items-center justify-center">
              {headerComponent.format === 'IMAGE' && (
                (() => {
                  const mediaUrl = (headerComponent as any)?.mediaUrl;
                  const exampleHandle = (headerComponent as any)?.example?.header_handle?.[0];
                  const exampleUrl = (headerComponent as any)?.example?.header_url?.[0];

                  const mediaSrc = mediaUrl || exampleUrl || exampleHandle;

                  const isValidPreviewUrl = mediaSrc && (
                    mediaSrc.startsWith('blob:') ||
                    mediaSrc.startsWith('http') ||
                    mediaSrc.startsWith('data:')
                  );

                  return isValidPreviewUrl ? (
                    <img src={mediaSrc} alt="Header" className="max-h-40 rounded" />
                  ) : (
                    <div className="text-center p-4">
                      <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">Image Preview</p>
                    </div>
                  );
                })()
              )}
              {headerComponent.format === 'VIDEO' && (
                (() => {
                  const mediaUrl = (headerComponent as any)?.mediaUrl;
                  const exampleHandle = (headerComponent as any)?.example?.header_handle?.[0];
                  const exampleUrl = (headerComponent as any)?.example?.header_url?.[0];

                  const mediaSrc = mediaUrl || exampleUrl || exampleHandle;

                  const isValidPreviewUrl = mediaSrc && (
                    mediaSrc.startsWith('blob:') ||
                    mediaSrc.startsWith('http') ||
                    mediaSrc.startsWith('data:')
                  );

                  return isValidPreviewUrl ? (
                    <video src={mediaSrc} className="max-h-48 rounded" controls muted />
                  ) : (
                    <div className="text-center p-4">
                      <Video className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">Video Preview</p>
                    </div>
                  );
                })()
              )}
              {headerComponent.format === 'DOCUMENT' && (
                (() => {
                  const mediaUrl = (headerComponent as any)?.mediaUrl;
                  const exampleHandle = (headerComponent as any)?.example?.header_handle?.[0];
                  const exampleUrl = (headerComponent as any)?.example?.header_url?.[0];

                  const mediaSrc = mediaUrl || exampleUrl || exampleHandle;

                  const isValidPreviewUrl = mediaSrc && (
                    mediaSrc.startsWith('blob:') ||
                    mediaSrc.startsWith('http') ||
                    mediaSrc.startsWith('data:')
                  );

                  return (
                    <div className="text-center p-4">
                      <FileText className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      {isValidPreviewUrl ? (
                        <a href={mediaSrc} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">
                          Open Document
                        </a>
                      ) : (
                        <p className="text-xs text-gray-500">Document Preview</p>
                      )}
                    </div>
                  );
                })()
              )}
              {headerComponent.format === 'LOCATION' && (
                <div className="text-center p-4">
                  <MapPin className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-xs text-gray-500">Location Preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFooter = () => {
    const category = (template?.category || '').toString().toUpperCase();
    let footerText: string | undefined;
    if (category === 'AUTHENTICATION' && footerComponent?.code_expiration_minutes) {
      const minutes = Number(footerComponent.code_expiration_minutes);
      if (Number.isFinite(minutes) && minutes > 0) {
        footerText = `Expires in ${minutes} minutes.`;
      }
    }
    if (!footerText) {
      footerText = footerComponent?.text;
    }
    if (!footerText) return null;

    return (
      <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200 break-words [overflow-wrap:anywhere] min-w-0">
        {footerText}
      </div>
    );
  };

  const renderButtons = () => {
    if (!buttonsComponent) {
      return null;
    }

    let buttons: Array<{ text: string; type?: string; url?: string; phone_number?: string }> = [];

    if (typeof buttonsComponent.text === 'string' && buttonsComponent.text.includes('|')) {
      buttons = (buttonsComponent.text as string)
        .split('|')
        .map((t: string) => ({ text: t.trim() }))
        .filter((b: any) => !!b.text);
    } else if (Array.isArray((buttonsComponent as any).buttons)) {
      buttons = (buttonsComponent as any).buttons as any[];
    } else if (Array.isArray((buttonsComponent as any).quickReply)) {
      buttons = ((buttonsComponent as any).quickReply as any[]).map((b: any) => ({ text: b.text }));
    } else if (Array.isArray((buttonsComponent as any).callToAction)) {
      buttons = (buttonsComponent as any).callToAction as any[];
    }

    if (!buttons.length) {
      return null;
    }

    return (
      <div className="space-y-2 mt-3">
        {buttons.map((button, index) => (
          <button
            key={index}
            title={button.url || button.phone_number || ''}
            className="w-full bg-blue-500 text-white text-sm py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 min-w-0"
          >
            {getButtonIcon((button.type || '').toUpperCase())}
            <span className="truncate">{button.text}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderCarousel = () => {
    if (!carouselComponent?.cards || !Array.isArray(carouselComponent.cards) || carouselComponent.cards.length === 0) return null;
    const cards = carouselComponent.cards as any[];

    const resolveCardParts = (card: any) => {
      const headerComp = card?.header || card?.components?.find?.((c: any) => (c?.type || '').toUpperCase() === 'HEADER');
      const bodyComp = card?.body || card?.components?.find?.((c: any) => (c?.type || '').toUpperCase() === 'BODY');
      const buttonsComp = card?.buttons || card?.components?.find?.((c: any) => (c?.type || '').toUpperCase() === 'BUTTONS');
      const buttons = Array.isArray(buttonsComp?.buttons) ? buttonsComp.buttons : [];

      const mediaUrl =
        headerComp?.mediaUrl ||
        headerComp?.url ||
        headerComp?.example?.header_url?.[0] ||
        headerComp?.example?.header_handle?.[0] ||
        card?.mediaUrl;

      const isValidPreviewUrl =
        mediaUrl && typeof mediaUrl === 'string' &&
        (mediaUrl.startsWith('blob:') ||
          mediaUrl.startsWith('http') ||
          mediaUrl.startsWith('https') ||
          mediaUrl.startsWith('data:'));

      const bodyText = typeof bodyComp === 'string' ? bodyComp : bodyComp?.text;

      return { headerComp, bodyComp, bodyText, buttons, mediaUrl: isValidPreviewUrl ? mediaUrl : null };
    };

    return (
      <div className="space-y-3 mt-4 border-t border-gray-100 pt-4">
        {carouselComponent.headerText && (
          <div className="text-[13px] font-semibold text-gray-700 px-1">
            {carouselComponent.headerText}
          </div>
        )}
        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide px-1">
          {cards.map((card, index) => {
            const { headerComp, bodyText, buttons, mediaUrl } = resolveCardParts(card);
            return (
              <div
                key={index}
                className="min-w-[220px] max-w-[240px] bg-white border rounded-lg shadow-sm overflow-hidden"
              >
                <div className="h-32 bg-gray-100 flex items-center justify-center relative overflow-hidden group">
                  {mediaUrl ? (
                    (() => {
                      const isVideo = headerComp?.format === 'VIDEO';
                      const isDocument = headerComp?.format === 'DOCUMENT';

                      if (isVideo) {
                        return (
                          <div className="relative w-full h-full flex items-center justify-center bg-black">
                            <Video className="h-8 w-8 text-white/50" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center backdrop-blur-sm">
                                <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent ml-1" />
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (isDocument) {
                        return (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 p-4">
                            <FileText className="h-10 w-10 text-blue-500 mb-2" />
                            <span className="text-[10px] text-gray-500 font-medium truncate w-full text-center">Document.pdf</span>
                          </div>
                        );
                      }

                      return <img src={mediaUrl} alt="Card media" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />;
                    })()
                  ) : (
                    <div className="text-center text-gray-400 text-[10px] flex flex-col items-center gap-1">
                      {headerComp?.format === 'VIDEO' ? <Video className="h-5 w-5" /> :
                        headerComp?.format === 'DOCUMENT' ? <FileText className="h-5 w-5" /> :
                          <Image className="h-5 w-5" />}
                      <span>[{headerComp?.format || 'Media'}]</span>
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1 min-h-[100px]">
                  {headerComp?.text && headerComp?.format === 'TEXT' && (
                    <div className="text-[13px] font-bold text-gray-900 mb-1 line-clamp-1">
                      {headerComp.text}
                    </div>
                  )}
                  <div className="text-[12px] text-gray-800 leading-snug whitespace-pre-wrap flex-1">
                    {bodyText || 'Card body text'}
                  </div>
                  {buttons.length > 0 && (
                    <div className="space-y-1">
                      {buttons.slice(0, 2).map((btn: any, i: number) => (
                        <button
                          key={i}
                          className="w-full bg-blue-500 text-white text-[10px] py-1.5 px-2 rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center gap-1.5"
                        >
                          {getButtonIcon((btn.type || '').toUpperCase())}
                          <span className="truncate">{btn.text || btn.url || btn.phone_number || 'Button'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white dark:bg-[#202c33] rounded-lg shadow-sm border dark:border-gray-800 p-3 space-y-3 w-full min-w-0 overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
          W
        </div>
        <div className="text-xs font-medium dark:text-white">WhatsApp Business</div>
        <div className="text-[10px] text-gray-500 ml-auto">now</div>
      </div>

      <div className="bg-gray-100 dark:bg-[#111b21] rounded-lg p-2 space-y-2 min-w-0 overflow-hidden">
        {renderHeader()}
        <div className="text-[13px] text-gray-800 dark:text-[#e9edef] leading-relaxed break-words whitespace-pre-wrap [overflow-wrap:anywhere] min-w-0">
          {getTemplatePreviewText(template)}
        </div>
        {renderFooter()}
        {renderButtons()}
        {renderCarousel()}
      </div>
    </div>
  );
}
